<script lang="ts" setup>
import { CalculatedFieldConditionType } from '@/core/collections/contracts-api/calcuclated-field-condition-types/calculated-field-condition-type.interface';
import { PropertyType } from '@/core/collections/contracts-api/companies/property-types/property-type.interface';
import { ContractTemplateFieldName } from '@/core/collections/contracts-api/contract-template-field-names/contract-template-field-name.interface';
import {
   ContractTemplateCalculatedValue,
   ContractTemplateCondition,
   ContractTemplateField,
} from '@/core/collections/contracts-api/customers/contract-templates/contract-template.interface';
import {
   cloneCalculatedValuesFormData,
   ContractTemplateCalculatedValuesFormData,
   mapContractTemplateCalculatedValuesFormErrors,
   useContractTemplateCalculatedValuesSchema,
} from '@/core/collections/contracts-api/customers/contract-templates/contract-template-calculated-values.schema';
import { SalaryIndexationLog } from '@/core/collections/contracts-api/employers/salary-indexations/salary-indexation.interface';
import { useStore } from '@/core/hooks/useStore';
import { NxtGenericFormModal, NxtGlobalModalKey, NxtLink, useGlobalModal } from '@nxtpeople/nxt-styles';
import dayjs from 'dayjs';
import { PropType, nextTick, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import ContractTemplateCalculatedValueItem from './ContractTemplateCalculatedValueItem.vue';
import ContractTemplateWizardField from './ContractTemplateWizardField.vue';
import { useContractTemplateConditionKeys } from './useContractTemplateConditionKeys';

//#region Globals
const { t } = useI18n();
const { dispatch } = useStore();
const { openModal } = useGlobalModal();
const { CALCULATED_FIELD_CONDITION_TYPE_KEYS } = useContractTemplateConditionKeys();
const companyId = inject('companyId', ref<number>());

const salaryTypeField = inject('salaryTypeField', ref({} as ContractTemplateField));
const updateSalaryTypeField = inject('updateSalaryTypeField', (field?: ContractTemplateField) => null);
const salaryTypeFieldName = inject('salaryTypeFieldName', ref<ContractTemplateFieldName>());

const modalFormData = ref<ContractTemplateCalculatedValuesFormData>({
   field: {} as ContractTemplateField,
   salary_type_field: {} as ContractTemplateField,
   calculated_values: [],
   salary_type_calculated_values: [],
});
const formMountKey = ref(0);
const isModalReady = ref(false);
//#endregion

//#region Props & Emits
const emits = defineEmits(['onCancel', 'onSave']);
const props = defineProps({
   loading: { type: Boolean, default: false },
   field: { type: Object as PropType<ContractTemplateField>, default: null },
   fieldName: { type: Object as PropType<ContractTemplateFieldName>, default: null },
});
const { CALCULATED_VALUES_SCHEMA } = useContractTemplateCalculatedValuesSchema(props.fieldName, salaryTypeFieldName.value);
//#endregion

//#region Condition Types
const loadingConditionTypes = ref(false);
const conditionTypes = ref<Array<CalculatedFieldConditionType>>([]);
const propertyTypes = ref<Array<PropertyType>>([]);

function mapCalculatedValues(fieldData: ContractTemplateField, companyProperty?: CalculatedFieldConditionType) {
   return [...fieldData.calculated_values].map((cv) => ({
      ...cv,
      conditions: cv.conditions.map((c) => {
         if (c.calculated_field_condition_type_id === companyProperty?.id) {
            const conditionType = conditionTypes.value.find(
               (ct) => ct.id === `${c.calculated_field_condition_type_id}-${c.selected_value.company_property_type_id}`,
            );

            if (conditionType)
               return {
                  ...c,
                  calculated_field_condition_type_id: `${c.calculated_field_condition_type_id}-${c.selected_value.company_property_type_id}`,
                  calculated_field_condition_type: {
                     ...conditionType,
                     id: `${c.calculated_field_condition_type_id}-${c.selected_value.company_property_type_id}`,
                  },
               };
         }

         const conditionType = conditionTypes.value.find((ct) => ct.id === c.calculated_field_condition_type_id);
         return { ...c, calculated_field_condition_type: conditionType };
      }),
   }));
}

onMounted(async () => {
   loadingConditionTypes.value = true;

   try {
      const fieldData = { ...props.field };
      const salaryTypeFieldData = { ...salaryTypeField.value };

      const response: Array<CalculatedFieldConditionType> = await dispatch('calculatedFieldConditionType/getCalculatedFieldConditionTypes');
      conditionTypes.value = mapConditionTypes(response.filter((ct) => ct.key !== 'company_property'));

      const companyProperty = response.find((ct) => ct.key === 'company_property');
      if (companyProperty) {
         propertyTypes.value = await dispatch('company/propertyType/getPropertyTypes', {
            query: { per_page: 999, page: 1 },
            companyId: companyId.value,
         }).then((response) => response.data);
         conditionTypes.value = conditionTypes.value.concat(mapPropertyTypes(propertyTypes.value, companyProperty));
      }

      if ((fieldData?.calculated_values || [])?.length) {
         fieldData.calculated_values = mapCalculatedValues(fieldData, companyProperty);
      } else {
         fieldData.calculated_values = [{ conditions: [{} as ContractTemplateCondition] } as ContractTemplateCalculatedValue];
      }

      if ((salaryTypeFieldData?.calculated_values || [])?.length) {
         salaryTypeFieldData.calculated_values = mapCalculatedValues(salaryTypeFieldData, companyProperty);
      } else {
         salaryTypeFieldData.calculated_values = [{ conditions: [{} as ContractTemplateCondition] } as ContractTemplateCalculatedValue];
      }

      modalFormData.value = cloneCalculatedValuesFormData(fieldData, salaryTypeFieldData);
      await nextTick();
      formMountKey.value += 1;
      isModalReady.value = true;
   } finally {
      loadingConditionTypes.value = false;
   }
});

function mapConditionTypes(calculatedFieldConditionTypes: Array<CalculatedFieldConditionType>) {
   return calculatedFieldConditionTypes.map((cto) => {
      const fieldKey = CALCULATED_FIELD_CONDITION_TYPE_KEYS.find(({ key }) => key === cto.key);

      if (fieldKey) {
         return {
            ...cto,
            label: fieldKey?.translation,
            options: fieldKey?.options,
            optionsUrl: fieldKey?.optionsUrl,
            display_key: fieldKey?.display_key,
         };
      }
      return cto;
   });
}

function mapPropertyTypes(
   propertyTypes: Array<PropertyType>,
   companyProperty: CalculatedFieldConditionType,
): CalculatedFieldConditionType[] {
   return propertyTypes.map(
      (pt) =>
         ({
            ...companyProperty,
            id: `${companyProperty.id}-${pt.id}`,
            key: pt.name,
            name: pt.name,
            resource_endpoint: 'company_property',
            label: pt.name,
            options: pt.properties,
         }) as CalculatedFieldConditionType,
   );
}
//#endregion

//#region Calculated Values
function addCalculatedValue(formData: ContractTemplateCalculatedValuesFormData) {
   formData.salary_type_calculated_values?.push({ conditions: [{} as ContractTemplateCondition] } as ContractTemplateCalculatedValue);
   formData.calculated_values.push({ conditions: [{} as ContractTemplateCondition] } as ContractTemplateCalculatedValue);
}

function removeCalculatedValue(formData: ContractTemplateCalculatedValuesFormData, index: number) {
   formData.calculated_values.splice(index, 1);
   formData.salary_type_calculated_values?.splice(index, 1);
}
//#endregion

//#region Save Calculated Values
function handleSave(formData: ContractTemplateCalculatedValuesFormData) {
   const fieldData: ContractTemplateField = {
      ...formData.field,
      calculated_values: formData.calculated_values,
   };

   updateSalaryTypeField({
      ...formData.salary_type_field,
      calculated_values: formData.calculated_values.map((cv, index) => ({
         ...cv,
         value: formData.salary_type_calculated_values[index].value,
         selected_value: formData.salary_type_calculated_values[index].selected_value,
      })),
   });

   emits('onSave', fieldData);
}
//#endregion

//#region Salary Indexation
function showIndexationLog(salaryIndexationLogs: Array<SalaryIndexationLog>) {
   const logs = salaryIndexationLogs.map(
      (salaryIndexationLog) => `
               <div class='d-flex align-items-center gap-2'>
                  <strong>${dayjs(salaryIndexationLog.indexation_date).format('DD/MM/YYYY')}</strong>
                  <span>${salaryIndexationLog.old_value}</span>
                  <i class='fas fa-arrow-right'></i>
                  <span>${salaryIndexationLog.indexation_value}</span>
               </div>
            `,
   );

   openModal(NxtGlobalModalKey.CONFIRMATION, {
      title: t('general.indexation-history'),
      cancelText: null,
      confirmText: t('actions.close'),
      warningMessage: {
         html: `
            <div class="d-flex flex-column gap-3" style="max-height: 20remTo; overflow-y: auto;">
               ${logs.join('')}
            </div>`,
         type: 'warning',
      },
   });
}
//#endregion
</script>

<template>
   <NxtGenericFormModal
      v-if="isModalReady"
      :key="`calculated-values-${formMountKey}`"
      class="contract-template-calculated-values-form"
      :data="modalFormData"
      :modal-type="'custom'"
      :custom-title="`Voeg berekende waarden toe`"
      :primary-action-text="t('actions.save')"
      :loading="loading || loadingConditionTypes"
      :schema="CALCULATED_VALUES_SCHEMA"
      @close="emits('onCancel')"
      @save="handleSave"
   >
      <template #default="{ modelValue, errors, loading: formLoading }">
         <div class="contract-template-calculated-values-form__calculated-values">
            <div class="d-flex gap-3 align-items-center">
               <ContractTemplateWizardField
                  :style="{ width: '23.84rem' }"
                  :field="modelValue.field"
                  :field-name="fieldName"
                  :mode="'light'"
                  :disabled="formLoading"
                  :errors="mapContractTemplateCalculatedValuesFormErrors(errors)"
                  :hide-calculated-values="true"
                  @on-update-field="({ key, value }) => ((modelValue.field as any)[key] = value)"
               />
               <ContractTemplateWizardField
                  v-if="salaryTypeFieldName"
                  :style="{ width: '23.84rem' }"
                  :field="modelValue.salary_type_field"
                  :field-name="salaryTypeFieldName"
                  :mode="'light'"
                  :disabled="formLoading"
                  :errors="mapContractTemplateCalculatedValuesFormErrors(errors)"
                  :hide-calculated-values="true"
                  @on-update-field="({ key, value }) => ((modelValue.salary_type_field as any)[key] = value)"
               />

               <div v-if="field?.salary_indexation_logs?.length" class="mt-4">
                  <NxtLink @on-click="showIndexationLog(field.salary_indexation_logs)">
                     {{ t('actions.show-indexation-history') }}
                  </NxtLink>
               </div>
            </div>

            <ContractTemplateCalculatedValueItem
               v-for="(calculatedValue, index) in modelValue.calculated_values"
               :key="index"
               :calculated-value-index="index"
               :calculated-value="calculatedValue"
               :salary-type-calculated-value="modelValue.salary_type_calculated_values[index]"
               :condition-types="conditionTypes"
               :errors="mapContractTemplateCalculatedValuesFormErrors(errors)"
               :field="modelValue.field"
               :field-name="fieldName"
               :loading="loadingConditionTypes"
               @on-remove-calculated-value="removeCalculatedValue(modelValue, $event)"
               @update:calculated-value="modelValue.calculated_values[index] = $event"
               @update:salary-type-calculated-value="modelValue.salary_type_calculated_values[index] = $event"
               @show-indexation-modal="showIndexationLog"
            />

            <NxtLink @on-click="addCalculatedValue(modelValue)">
               + {{ t('actions.add-type', { name: t('companies.templates.calculated-value', 1) })?.toLowerCase() }}
            </NxtLink>
         </div>
      </template>
   </NxtGenericFormModal>
</template>

<style lang="scss" scoped>
.contract-template-calculated-values-form__calculated-values {
   display: flex;
   align-items: flex-start;
   flex-direction: column;
   gap: var(--nxt-gutter);
   width: 100%;
}
</style>
